



'use client';
import type React from 'react';
import { Buttons } from '../../../../components/ui/button';
import { useCallback, useRef } from 'react';
import { Badge } from '../../../../components/ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import svgPaths from '../../../../imports/svg-knollny4nu';
import svgQuestComplete from '../../../../imports/svg-f1z862fadf';
import {
  CustomCoinIcon,
  CustomHomeIcon,
  CustomMapIcon,
  CustomMedalIcon,
  CustomRankingIcon,
  CustomSettingsIcon,
} from '../../../../components/ui/CustomIcons';
import imgBackground from '../../../../assets/img/NewUI_Images/background.png';
import { useColor } from '../../../../components/ui/ColorContext';
import ColorPicker from '../../../../components/ui/ColorPicker';
import { Slider } from '../../../../components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from '../../../../components/ui/dialog';

import { background, Box, Img, Text } from '@chakra-ui/react';
import { startTransition, useContext, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ScoreContext } from '../GamePreview';
import BtnClickSound from "../../../../assets/adiuo/simple-btn-click.mp3";
import { getGameOverallQuestScore } from 'utils/gameApplication/gameActivityService';
import { questScore } from 'utils/game/gameService';
import { HudTransition } from './HudTransition';
import { useSoundEffects } from './Hooks/useSoundEffects';
import { ParticleField } from './ParticleField';
import { BackgroundBeam } from './BackgroundBeam';
import { TitlePulse } from './TitlePulse';
import { useCountUp } from './Hooks/useCountUp';
import { updateScreenActivity } from '../common';
// Helper function to convert hex to RGB
const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "0, 255, 187"; // fallback to default green
  const r = Number.parseInt(result[1], 16);
  const g = Number.parseInt(result[2], 16);
  const b = Number.parseInt(result[3], 16);
  return `${r}, ${g}, ${b}`;
};


const normalizeStarValue = (value: number): number => {
  const clamped = Math.max(0, Math.min(1, Number(value) || 0));
  if (clamped >= 0.999) return 1;
  if (clamped <= 0.001) return 0;
  return clamped;
};

const NavigationArrow: React.FC<{
  direction: 'left' | 'right';
  onClick: () => void;
}> = ({ direction, onClick }) => {
  const { primaryColor } = useColor();
  return (
    <Buttons
      variant="ghost"
      size="sm"
      onClick={onClick}
      className="h-10 w-[3.75rem] rounded-3xl px-4 py-2 transition-all duration-200 hover:scale-110"
      style={{
        background: `linear-gradient(${
          direction === 'left' ? '275.041deg' : '90deg'
        },
                rgba(${hexToRgb(primaryColor)}, 0.6) 7.25%, rgba(${hexToRgb(
          primaryColor,
        )}, 0.15) 84.803%)`,
        border: `1px solid ${primaryColor}`,
        boxShadow: `0px 0px 15.9542px 0px rgba(${hexToRgb(primaryColor)}, 0.3)`,
      }}
    >
      {direction === 'left' ? (
        <ChevronLeft className="!h-4 !w-4 text-white" />
      ) : (
        <ChevronRight className="!h-4 !w-4 text-white" />
      )}
    </Buttons>
  );
};

// Floating particles components
const FloatingParticles1: React.FC = () => {
  const particles = [
    { left: "calc(20% + 7.937rem)", top: "90vh" },
    { left: "calc(20% + 14.317rem)", top: "95vh" },
    { left: "calc(60% + 9.806rem)", top: "92vh" },
    { left: "calc(60% + 10.702rem)", top: "94vh" },
    { left: "calc(60% + 13.687rem)", top: "97vh" },
    { left: "calc(20% + 7.750rem)", top: "96vh" },
  ];

  return (
    <>
      {particles.map((particle, index) => (
        <div
          key={index}
          className="absolute bg-white/40 rounded-full w-[2px] h-[2px] particle-float"
          style={{
            left: particle.left,
            top: particle.top,
            animationDuration: `${6 + Math.random() * 6}s`,
            animationDelay: `${Math.random() * 5}s`,
          }}
        />
      ))}
    </>
  );
};

const FloatingParticles2: React.FC = () => {
  const particles = [
    { left: "calc(20% + 4.607rem)" },
    { left: "calc(20% + 11.077rem)" },
    { left: "calc(60% + 7.011rem)" },
    { left: "calc(60% + 7.919rem)" },
    { left: "calc(60% + 10.946rem)" },
    { left: "calc(20% + 4.438rem)" },
    { left: "calc(20% + 16.109rem)" },
    { left: "calc(20% + 16.487rem)" },
    { left: "calc(60% + 0.465rem)" },
    { left: "calc(60% + 2.206rem)" },
    { left: "calc(20% + 4.985rem)" },
    { left: "calc(20% + 11.720rem)" },
    { left: "calc(60% + 1.828rem)" },
    { left: "calc(60% + 13.196rem)" },
    { left: "calc(60% + 3.719rem)" },
    { left: "calc(20% + 13.877rem)" },
    { left: "calc(20% + 5.552rem)" },
    { left: "calc(60% + 11.325rem)" },
    { left: "calc(60% + 5.649rem)" },
  ];

  return (
    <>
      {particles.map((particle, index) => (
        <div
          key={index}
          className="absolute bg-white/60 rounded-full w-[2px] h-[2px] particle-float"
          style={{
            left: particle.left,
            bottom: "-10px", // start from bottom
            animationDuration: `${8 + Math.random() * 6}s`,
            animationDelay: `${Math.random() * 6}s`,
          }}
        />
      ))}
    </>
  );
};

const FloatingParticles3: React.FC = () => {
  const particles = [
    { left: "calc(20% + 4.374rem)" },
    { left: "calc(20% + 10.754rem)" },
    { left: "calc(60% + 6.244rem)" },
    { left: "calc(60% + 7.139rem)" },
    { left: "calc(60% + 10.124rem)" },
    { left: "calc(20% + 4.188rem)" },
    { left: "calc(20% + 15.716rem)" },
    { left: "calc(20% + 16.090rem)" },
    { left: "calc(60% - 0.211rem)" },
    { left: "calc(60% + 1.506rem)" },
    { left: "calc(20% + 4.747rem)" },
    { left: "calc(20% + 11.388rem)" },
    { left: "calc(60% + 1.133rem)" },
    { left: "calc(60% + 12.363rem)" },
    { left: "calc(60% + 2.998rem)" },
    { left: "calc(20% + 13.515rem)" },
    { left: "calc(20% + 5.307rem)" },
    { left: "calc(60% + 10.497rem)" },
    { left: "calc(60% + 4.901rem)" },
  ];

  return (
    <>
      {particles.map((particle, index) => (
        <div
          key={index}
          className="absolute bg-white/50 rounded-full w-[1.5px] h-[1.5px] particle-float"
          style={{
            left: particle.left,
            bottom: "-10px",
            animationDuration: `${10 + Math.random() * 8}s`,
            animationDelay: `${Math.random() * 8}s`,
          }}
        />
      ))}
    </>
  );
};

  const getRating = (stars: number): string => {
  switch(stars) {
    case 1: return 'DEVELOPING';
    case 2: return 'STRONG';
    case 3: return 'EXCELLENT';
    default: return '';
  }
};





interface StarsCardProps {
  filledStars: number[]; // [1, 0.35, 0]
  totalStars: number;
  visible?: boolean;
  animationDelay?: number;
  onStarIgnite?: () => void;
}

interface StarProps {
  isLit: boolean;
  fillPercentage: number; // 0 → 1
  index: number;
  shouldAnimate: boolean;
  pulseComplete: boolean;
  primaryColor: string;
}

const Star = ({
  isLit,
  fillPercentage,
  index,
  shouldAnimate,
  pulseComplete,
  primaryColor,
}: StarProps) => (
  <div
    className="aura-wrapper relative w-12 h-12"
    style={{ animationDelay: `${index * 300}ms` }}
  >
    {/* Pulse ring */}
    {isLit && shouldAnimate && !pulseComplete && (
      <div className="pulse-ring absolute left-1/2 top-1/2" />
    )}

    <div className={`starburst ${isLit ? 'filled' : 'empty'} ${isLit && shouldAnimate ? 'star-pop' : ''}`}
      style={isLit ? {
        filter: `drop-shadow(0 0 12px ${primaryColor}) drop-shadow(0 0 3px #fff)`
      } : undefined}
    >
      <svg viewBox="0 0 24 24">
        <defs>
          {/* Base gradient */}
          <linearGradient
            id={`plasmaGrad-${index}`}
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor={`rgba(${hexToRgb(primaryColor)}, 1)`} />
            <stop offset="50%" stopColor={`rgba(${hexToRgb(primaryColor)}, 0.8)`} />
            <stop offset="100%" stopColor={`rgba(${hexToRgb(primaryColor)}, 0.6)`} />
          </linearGradient>

          {/* PARTIAL FILL MASK */}
          <linearGradient id={`fillMask-${index}`}>
            <stop offset={`${fillPercentage * 100}%`} stopColor="white" />
            <stop offset={`${fillPercentage * 100}%`} stopColor="black" />
          </linearGradient>

          <mask id={`starMask-${index}`}>
            <rect width="24" height="24" fill={`url(#fillMask-${index})`} />
          </mask>
        </defs>

        {isLit ? (
            <g className="star-fill">
              <polygon
                className="star-body"
                points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"
                fill={`url(#plasmaGrad-${index})`}
                mask={`url(#starMask-${index})`}
                stroke={`rgba(${hexToRgb(primaryColor)}, 1)`}
                strokeWidth="1"
              />
              <circle className="star-glow" cx="12" cy="12" r="2.4" fill="rgba(255,255,255,0.95)" />
            </g>
        ) : (
          <g className="star-empty">
            <polygon
              className="star-body"
              points="12,2 15,9 22,9 17,14 19,21 12,17 5,21 7,14 2,9 9,9"
              fill="transparent"
              stroke={`rgba(${hexToRgb(primaryColor)}, 0.4)`}
              strokeWidth="1"
            />
          </g>
        )}
      </svg>
    </div>

    {/* Aura ring */}
    {isLit && (
      <svg
        className="absolute pointer-events-none"
        style={{
          top: '-6px',
          left: '-6px',
          width: 'calc(100% + 12px)',
          height: 'calc(100% + 12px)',
        }}
        viewBox="0 0 40 40"
      >
        <circle
          cx="20"
          cy="20"
          r="17"
          fill="none"
          stroke={`rgba(${hexToRgb(primaryColor)}, 0.9)`}
          strokeWidth="1.2"
          strokeDasharray="8 12"
          strokeDashoffset="6"
        >
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 20 20"
            to="360 20 20"
            dur="4s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    )}
  </div>
);

export const StarsCard = ({
  filledStars,
  totalStars,
  visible = true,
  animationDelay = 0,
  onStarIgnite,
}: StarsCardProps) => {
  const [litStars, setLitStars] = useState(0);
  const [showRating, setShowRating] = useState(false);
  const [animatedStars, setAnimatedStars] = useState<Set<number>>(new Set());
  const [completedPulseAnimations, setCompletedPulseAnimations] =
    useState<Set<number>>(new Set());


  const filledStarsKey = filledStars.join('|');

useEffect(() => {
  if (!visible) return;

    const timeouts: number[] = [];

    // Sequential star ignition
    filledStars.forEach((fill, i) => {
      if (fill === 1) {
        timeouts.push(
          window.setTimeout(() => {
            setLitStars(i + 1);
            setAnimatedStars(prev => new Set(prev).add(i));
            onStarIgnite?.();
          }, animationDelay + i * 400)
        );

      timeouts.push(
        window.setTimeout(() => {
          setCompletedPulseAnimations(prev => new Set(prev).add(i));
        }, animationDelay + i * 400 + 700),
      );
    }
  });

  timeouts.push(
    window.setTimeout(() => {
      setShowRating(true);
    }, animationDelay + filledStars.length * 400 + 200),
  );

    return () => timeouts.forEach(clearTimeout);

  }, [visible, filledStarsKey, animationDelay, onStarIgnite]);

  const rating = getRating(filledStars.filter(v => v === 1).length);
  const { primaryColor } = useColor();

  return (
   
    <div
      className={`reward-card w-[16.5rem] h-[16.5rem] rounded-[1.1rem] p-5 relative flex flex-col items-center justify-start gap-6 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
        }`}
    >
     
      <span className="relative z-10 font-rubik font-bold text-base text-white tracking-wider">
        Stars
      </span>

      <div className="relative z-10 flex flex-col items-center mt-3 w-full">
        <div className="flex justify-center items-center gap-4 mb-5">
          {Array.from({ length: totalStars }).map((_, i) => (
            <Star
              key={i}
              isLit={filledStars[i] > 0}
              fillPercentage={filledStars[i] ?? 0}
              index={i}
              shouldAnimate={animatedStars.has(i)}
              pulseComplete={completedPulseAnimations.has(i)}
              primaryColor={primaryColor}
            />
          ))}
        </div>

        {/* <div data-aos="zoom-out" data-aos-delay="2500" data-aos-duration="700" data-aos-easing="ease-out-sine"> */}
        <span
          className={`mt-9 text-[9px] font-orbitron tracking-[0.25em] transition-all duration-500 ${showRating ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
          style={{ color: primaryColor }}
        >
          {rating}
        </span>
      </div>
      {/* </div> */}
      {/* </div> */}
      {/* </div> */}
      {/* </div> */}
      {/* <div
        className="absolute border inset-0 pointer-events-none rounded-[1.402rem]"
        style={{
          borderColor: `rgba(${hexToRgb(primaryColor)}, 0.2)`,
          boxShadow: `0px 3.58px 15px 0px rgba(${hexToRgb(
            primaryColor,
          )}, 0.2)`,
        }}
      /> */}
    </div>
  );
};





const RewardCard: React.FC<{
  title: string;
  progress?: number;
  maxProgress?: number;
  icon: 'points' | 'trophy' | 'badge';
  badgeImage?: string;
  badgeName?: string;
  showMissedMessage?: boolean;
  visible?: boolean;
  animateStamp?: boolean;
  stampDelay?: number;
  animateValue?: boolean;
  animationDelay?: number;
}> = ({ title, animateStamp = false, stampDelay = 0, progress, maxProgress, icon, badgeImage, badgeName, showMissedMessage, visible = true, animateValue = false, animationDelay = 0, }) => {
  const { primaryColor } = useColor();
  const [stamped, setStamped] = useState(false);

  useEffect(() => {
    if (!animateStamp) return;

    const timer = setTimeout(() => {
      setStamped(true);
    }, stampDelay);

    return () => clearTimeout(timer);
  }, [animateStamp, stampDelay]);

  // Use useCountUp for animated progress
  const animatedValue = useCountUp({
    end: animateValue ? progress : 0,
    duration: 1200,
    delay: animateValue ? animationDelay : 0,
  });

  // Display value for UI
  const displayValue = animateValue
    ? `${animatedValue}/${maxProgress}`
    : `0/${maxProgress}`;

  // Determine if we're currently counting (for glow effect)
  const isCounting = animateValue && animatedValue < progress;

  // Track if counting just finished for flash effect
  const [countingFinished, setCountingFinished] = useState(false);
  const prevIsCounting = useRef(isCounting);

  // Track when card has revealed for score-reveal animation
  const [hasRevealed, setHasRevealed] = useState(false);

  useEffect(() => {
    if (visible && !hasRevealed) {
      const timer = setTimeout(() => setHasRevealed(true), animationDelay);
      return () => clearTimeout(timer);
    }
  }, [visible, animationDelay, hasRevealed]);

  // Detect when counting finishes and trigger flash
  useEffect(() => {
    if (prevIsCounting.current && !isCounting && animateValue) {
      setCountingFinished(true);
      const timer = setTimeout(() => setCountingFinished(false), 600);
      return () => clearTimeout(timer);
    }
    prevIsCounting.current = isCounting;
  }, [isCounting, animateValue]);

  return (
    <div
      className={`reward-card w-[16.5rem] h-[16.5rem] rounded-[1.1rem] p-5 relative flex flex-col items-center justify-center gap-6 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-8 scale-95'
        }`}
    >
     
	  {!showMissedMessage && (
      <span className="relative z-10 font-rubik font-bold text-xl text-white tracking-wider">
        {title}
      </span>
       )}
     
      {icon === "points" && progress !== undefined && maxProgress !== undefined && (
        <div className="relative z-10 flex flex-col items-center mt-3">
          <div
            className="mt-5 h-10 px-5 flex items-center rounded-xl border transition-all duration-150"
            style={{
              borderColor: "color-mix(in srgb, var(--dynamic-primary) 30%, transparent)",
              background:
                "linear-gradient(170.484deg, rgb(0,0,0) 17.606%, color-mix(in srgb, var(--dynamic-primary) 50%, transparent) 188.68%)"
            }}
          >
            <div className={`text-white font-orbitron text-xl tracking-[0.0450rem] ${isCounting ? 'score-glow score-pulse' : ''} ${countingFinished ? 'score-complete-flash' : ''}`}>
              {displayValue}
            </div>
          </div>

        </div>
      )}

     

      {icon === 'trophy' && (

        <div className="relative z-10 mt-1">
          <div className={`image-frame w-[108px] h-[108px] rounded-xl flex flex-col items-center justify-center overflow-hidden ${stamped ? 'badge-stamped' : ''
            }`}>
            <div className={`transition-all duration-300 ${stamped ? 'stamp-animation' : 'opacity-0 scale-150'}`}>
              <CustomMedalIcon
                color={primaryColor}
                className="!size-28 !size-16"
              />
		      
		      </div>
                    </div>
                </div>

            )}

    
      {icon === 'badge' && (
        <div className="relative z-10 mt-1">
          {showMissedMessage ? (
            <>
              
              <span className="text-white text-sm font-rubik text-center">
                You missed the reward!
              </span>
            </>
          ) : badgeImage ? (
            <>
              <div className={`image-frame w-[14rem] h-[10rem] rounded-xl flex flex-col items-center justify-center overflow-hidden ${stamped ? 'badge-stamped' : ''
                }`}>
                <div className={`transition-all duration-300 ${stamped ? 'stamp-animation' : 'opacity-0 scale-150'}`}>
                  <img
                    src={badgeImage}
                    alt={badgeName || 'Badge'}
                    className="w-32 h-32 object-contain"
                  />
                </div>
              </div>
                    {badgeName && !showMissedMessage && (
                      <Badge
                        variant="secondary"
                        className="h-8 px-3 flex items-center rounded-2xl font-rubik text-xs mt-1"
                        style={{
                          background: `linear-gradient(170.484deg, rgb(0, 0, 0) 17.606%, rgba(${hexToRgb(
                            primaryColor
                          )}, 0.5) 188.68%)`,
                          border: `1px solid rgba(${hexToRgb(primaryColor)}, 0.3)`,
                          boxShadow: "0px 0px 23.3864px 0px rgba(0,0,0,0.5)",
                        }}
                      >
                        <div className="text-white font-medium">
                          {badgeName}
                        </div>
                      </Badge>
                    )}
                  </>
                ) : (
                  <Buttons
                    variant="ghost"
                    size="sm"
                    className="w-28 h-28 !px-0 py-0 relative rounded-3xl cursor-default"
                    style={{
                      boxShadow: "0px 0px 23.386px 0px rgba(0, 0, 0, 0.50)",
                      background: `linear-gradient(151.477deg, rgba(${hexToRgb(
                        primaryColor
                      )}, 0.3), rgb(0, 0, 0))`,
                    }}
                  >
                    <div
                      className="absolute top-[1px] left-[1px] right-[1px] bottom-[1px] rounded-3xl flex items-center justify-center"
                      style={{
                        background: `linear-gradient(151.477deg, rgb(0, 0, 0) 17.606%, rgba(${hexToRgb(
                          primaryColor
                        )}, 0.5) 188.68%)`,
                        zIndex: 1,
                      }}
                    >
                      <CustomMedalIcon
                        color={primaryColor}
                        className="!size-28 !size-16"
                      />
                    </div>
                  </Buttons>
                )}
              </div>
            )}
    </div>
  );
};
const QuestCompletionFutureTheme: React.FC<{

    playMenuSound: () => void;
  onNavigate?: (direction: 'left' | 'right') => void;
  getscore: any;
  setIsCompletedVideoMap: any;
  completedVideoRef: any;
  iscompletedVideoMap: any;
  formData: any;
  imageSrc: any;
  questState: any;
  setType: any;
  setData: any;
  setQuestState: any;
  type: any;
  getFeedbackData: any;
  setCurrentScreenId?: any;
  currentScreenId: any;
  getData?: any;
  gameInfo: any;
  data?: any;
  setFeedbackNavigateNext: any;
  currentQuestNo: any;
  preloadedAssets: any;
  setOptions: any;
  profileData: any;
  feedbackList: any;
  setFeedbackList: any;
  learnerPlayList: any;
  setLearnerPlayingDetails: any;
  setprevBlock: any;
  prevBlock: any;
  demoBlocks: any;
  checkCompletionBadge: any;
  visible?: boolean;
  animationDelay?: number;
     WeHaveChooseCompleted?: any;
setWeHaveChooseCompleted?: any;

isSwipeMiniGameCompleted?: any;
setIsSwipeMiniGameCompleted?: any;

isDragForNowCompleted?: any;
setIsDragForNowCompleted?: any;
  // progress: number;
}> = ({
    playMenuSound,
        WeHaveChooseCompleted,
  setWeHaveChooseCompleted,
  isSwipeMiniGameCompleted,
  setIsSwipeMiniGameCompleted,
  isDragForNowCompleted,
  setIsDragForNowCompleted,
  iscompletedVideoMap,
  completedVideoRef,
  setIsCompletedVideoMap,
  onNavigate,
  formData,
  imageSrc,
  currentScreenId,
  setCurrentScreenId,
  gameInfo,
  setType,
  setData,
  type,
  questState,
  setQuestState,
  setFeedbackNavigateNext,
  getData,
  data,
  getFeedbackData,
  currentQuestNo,
  preloadedAssets,
  setOptions,
  profileData,
  setFeedbackList,
  feedbackList,
  learnerPlayList,
  setLearnerPlayingDetails,
  setprevBlock,
  prevBlock,
  demoBlocks,
  checkCompletionBadge,
  getscore
}) => {

    const { playSound, setEnabled } = useSoundEffects();

   const playClickSound = () => {
      try {
        const audio = new Audio(BtnClickSound as string);
        audio.volume = 1;
        audio.play().catch(() => { });

        setTimeout(() => {
          audio.pause();
          audio.currentTime = 0;
        }, 1000);
      } catch (e) {
        // ignore audio errors
      }
    };

  const { primaryColor } = useColor();
   const { profile, setProfile ,deviceInfo, setlearnerActivityrec} = useContext(ScoreContext);

  const [curretQuestOptions, setCurrentQuestOptions] = useState(
    gameInfo?.gameQuest?.find(
      (quest: any) => quest?.gameQuestNo == profile?.currentQuest,
    ),
  );
  const finalProfileScore =
    Object.entries(profile?.playerGrandTotal?.questScores).length > 0
      ? profile?.playerGrandTotal?.questScores[
          parseInt(profile.currentQuest)
        ] !== null
        ? profile?.playerGrandTotal?.questScores[parseInt(profile.currentQuest)]
        : curretQuestOptions?.gameMinScore
        ? curretQuestOptions?.gameMinScore
        : 0
      : curretQuestOptions?.gameMinScore
      ? curretQuestOptions?.gameMinScore
      : 0;
// const finalProfileScore =
//   profile?.currentQuest &&
//   ([...(profile?.score || []), ...(profile?.replayScore || [])]
//     .filter(
//       (item) => parseInt(item.quest) === parseInt(profile.currentQuest)
//     ).length > 0)
//     ? Math.max(
//         ...[...(profile?.score || []), ...(profile?.replayScore || [])]
//           .filter(
//             (item) => parseInt(item.quest) === parseInt(profile.currentQuest)
//           )
//           .map((item) => item.score || 0)
//       )
//     : curretQuestOptions?.gameMinScore
//     ? curretQuestOptions?.gameMinScore
//     : 0;
  const [geFinalscorequest, SetFinalscore] = useState(finalProfileScore);
  const [quetCompletionMessage, setQuestCompletionMessage] =
    useState<string>('');
  const [QuestTitleLanguage, setQuestTitleLanguage] = useState(null);
 
  const findQuestCompletionMessage = () => {
    const playerCurrentQuestGrandTotal =
      profile.playerGrandTotal.questScores[parseInt(currentQuestNo)] ?? 0;
    let completionScreenMessage = '';
    const getCompletionQuest = currentQuestNo - 1;

    const currentQuesTGameData = gameInfo?.gameQuest[getCompletionQuest];
    const isSingleMessage =
      currentQuesTGameData?.gameIsSetCongratsSingleMessage === 'true';
    const isScoreWiseMessage =
      currentQuesTGameData?.gameIsSetCongratsScoreWiseMessage === 'true';

    if (isSingleMessage) {
      const questContentFiltered = profileData?.Audiogetlanguage.filter(
        (key: any) => key?.textId === parseInt(currentQuestNo),
      );

      const congratsMsgFiltered = questContentFiltered?.filter(
        (key: any) => key?.fieldName === 'gameCompletedCongratsMessage',
      );

      completionScreenMessage = congratsMsgFiltered?.[0]?.content
        ? congratsMsgFiltered[0]?.content
        : currentQuesTGameData?.gameCompletedCongratsMessage;
    } else if (isScoreWiseMessage) {
      if (
        playerCurrentQuestGrandTotal >
        currentQuesTGameData?.gameDistinctionScore
      ) {
        if (currentQuesTGameData?.gameIsSetDistinctionScore === 'true') {
          completionScreenMessage =
            currentQuesTGameData?.gameAboveDistinctionScoreCongratsMessage;
        } else {
          if (
            playerCurrentQuestGrandTotal > currentQuesTGameData?.gameMinScore
          ) {
            completionScreenMessage =
              currentQuesTGameData?.gameaboveMinimumScoreCongratsMessage;
          }
        }
      } else if (
        playerCurrentQuestGrandTotal ===
        currentQuesTGameData?.gameDistinctionScore
      ) {
        if (currentQuesTGameData?.gameIsSetDistinctionScore === 'true') {
          completionScreenMessage =
            currentQuesTGameData?.gameAboveDistinctionScoreCongratsMessage;
        } else {
          if (
            playerCurrentQuestGrandTotal > currentQuesTGameData?.gameMinScore
          ) {
            completionScreenMessage =
              currentQuesTGameData?.gameaboveMinimumScoreCongratsMessage;
          }
        }
      } else if (
        playerCurrentQuestGrandTotal > currentQuesTGameData?.gameMinScore
      ) {
        completionScreenMessage =
          currentQuesTGameData?.gameaboveMinimumScoreCongratsMessage;
      } else if (
        playerCurrentQuestGrandTotal === currentQuesTGameData?.gameMinScore
      ) {
        completionScreenMessage =
          currentQuesTGameData?.gameaboveMinimumScoreCongratsMessage;
      } else if (
        playerCurrentQuestGrandTotal < currentQuesTGameData?.gameMinScore
      ) {
        if (currentQuesTGameData?.gameIsSetMinPassScore === 'true') {
          completionScreenMessage =
            currentQuesTGameData?.gameMinimumScoreCongratsMessage;
        } else {
          if (
            playerCurrentQuestGrandTotal <
            currentQuesTGameData?.gameDistinctionScore
          )
            completionScreenMessage =
              currentQuesTGameData?.gameaboveMinimumScoreCongratsMessage;
        }
      }
    } else if (
      !isSingleMessage &&
      !isScoreWiseMessage &&
      !currentQuesTGameData?.gameIsSetDistinctionScore &&
      !currentQuesTGameData?.gameIsSetMinPassScore
    ) {
      const questContentFiltered = profileData?.Audiogetlanguage.filter(
        (key: any) => key?.textId === parseInt(currentQuestNo),
      );

      const congratsMsgFiltered = questContentFiltered?.filter(
        (key: any) => key?.fieldName === 'gameCompletedCongratsMessage',
      );

      completionScreenMessage = congratsMsgFiltered?.[0]?.content
        ? congratsMsgFiltered[0]?.content
        : currentQuesTGameData?.gameCompletedCongratsMessage;
    } else {
      const questContentFiltered = profileData?.Audiogetlanguage.filter(
        (key: any) => key?.textId === parseInt(currentQuestNo),
      );
      const congratsMsgFiltered = questContentFiltered?.filter(
        (key: any) => key?.fieldName === 'gameCompletedCongratsMessage',
      );

      completionScreenMessage = congratsMsgFiltered?.[0]?.content
        ? congratsMsgFiltered[0]?.content
        : currentQuesTGameData?.gameCompletedCongratsMessage;
    }

    setQuestCompletionMessage(completionScreenMessage);
  };
  const updatelogactivityfunc = () => {

  setlearnerActivityrec((prev: any) =>
    updateScreenActivity(
      prev,
      6,
      "Completion",
      deviceInfo,
      {
        playerscore: Number(getscore),
        totalscore: Number(curretQuestOptions?.gameTotalScore),
      }
    )
  );
};
useEffect(() => {
  setWeHaveChooseCompleted(false);
  setIsSwipeMiniGameCompleted(false);
  setIsDragForNowCompleted(false);
  updatelogactivityfunc();
}, []);
  useEffect(() => {
    const findCurrentQuest =
      currentQuestNo === parseInt(profile?.currentQuest)
        ? parseInt(profile?.currentQuest)
        : currentQuestNo;
    const QuestContentFiltered = profileData?.Audiogetlanguage.filter(
      (key: any) => key?.textId === findCurrentQuest,
    );
    if (QuestContentFiltered.length > 0) {
      const gameTitleFiltered = QuestContentFiltered.filter(
        (key: any) => key?.fieldName === 'gameScreenTitle',
      );

      const FilteredTitlecontent = gameTitleFiltered[0]?.content
        ? gameTitleFiltered[0]?.content
        : curretQuestOptions?.gameScreenTitle;
      const screenTitleLanguage = FilteredTitlecontent;

      setQuestTitleLanguage(screenTitleLanguage);
    } else {
      setQuestTitleLanguage(curretQuestOptions?.gameScreenTitle);
    }

    findQuestCompletionMessage();
    // checkCompletionBadge();
    // getData();
  }, []);

  useEffect(() => {
    if (gameInfo?.gameQuest.length > 0) {
      const currentQuestData = gameInfo?.gameQuest?.find(
        (quest: any) => quest.gameQuestNo == profile?.currentQuest,
      );
      setCurrentQuestOptions(currentQuestData);
      findQuestCompletionMessage();
    }
  }, [gameInfo?.gameQuest]);
    useEffect(() => {
    }, [gameInfo])
////////////////////////////correct code////////////////////
  // const previousNavigation = () => {
  //    playClickSound();
  //   const questNo = profile?.currentQuest;
  //   // const Prevquestseq =learnerPlayList?.Prevquestseq[playingQuest];
  //   const lastSeq = learnerPlayList?.Prevquestseq[questNo];
  //   let updateNavigateSeq: any = [...learnerPlayList?.Prevquestseq[questNo]];

  //   updateNavigateSeq.pop();
  //   setLearnerPlayingDetails((prev: any) => ({
  //     ...prev,
  //     Prevquestseq: { ...prev.Prevquestseq, [questNo]: updateNavigateSeq },
  //   }));

  //   let prev =
  //     prevBlock.length > 0
  //       ? prevBlock[prevBlock.length - 1]
  //       : lastSeq
  //       ? lastSeq[lastSeq.length - 1]
  //       : null;

  //   let getBlocks: any;
  //   if (prev) {
  //     getBlocks = Object.values(demoBlocks[questNo]).filter(
  //       (item: any) => item?.blockPrimarySequence === prev,
  //     );
  //     if (prevBlock.length === 0 && updateNavigateSeq.length > 0) {
  //       setprevBlock(updateNavigateSeq);
  //     }
  //     prevBlock.pop();
  //     startTransition(() => {
  //       setData(getBlocks[0]);
  //       setType(getBlocks[0]?.blockChoosen);
  //       if (getBlocks[0]?.blockChoosen === 'Interaction') {
  //         const optionsFiltered = [];
  //         const primarySequence = getBlocks[0].blockPrimarySequence;

  //         for (const option of gameInfo.questOptions) {
  //           if (profileData?.Audiogetlanguage.length > 0) {
  //             if (option?.qpSequence === primarySequence) {
  //               const profilesetlan = profileData?.Audiogetlanguage.find(
  //                 (key: any) => key?.textId === option.qpOptionId,
  //               );

  //               if (profilesetlan) {
  //                 const languagecont = {
  //                   ...option,
  //                   qpOptionText: profilesetlan.content,
  //                 };
  //                 optionsFiltered.push(languagecont);
  //               } else {
  //                 optionsFiltered.push(option);
  //               }
  //             }
  //           } else {
  //             if (option?.qpSequence === primarySequence) {
  //               optionsFiltered.push(option);
  //             }
  //           }
  //         }
  //         if (gameInfo?.gameData?.gameShuffle === 'true') {
  //           for (let i = optionsFiltered.length - 1; i > 0; i--) {
  //             const j = Math.floor(Math.random() * (i + 1));
  //             [optionsFiltered[i], optionsFiltered[j]] = [
  //               optionsFiltered[j],
  //               optionsFiltered[i],
  //             ];
  //           }
  //         }
  //         setOptions(optionsFiltered);
  //       }
  //       if (questState && questState[profile?.currentQuest]) {
  //         if (questState[profile?.currentQuest] !== 'Started') {
  //           setProfile((prev: any) => ({
  //             ...prev,
  //             replayScore: profile.score,
  //           }));
  //         }
  //       }

  //       setCurrentScreenId(2);
  //     });
  //     return false;
  //   } else {
  //     startTransition(() => {
  //       setCurrentScreenId(13);
  //     });
  //   }
  // };
 ///////////////////////above correct code////////////////

 const previousNavigation = () => {
     playClickSound();
    // Use the currently viewed quest, not the profile's latest unlocked quest.
    // Otherwise, back navigation can pop the wrong quest history after switching quests.
    const questNo = String(currentQuestNo ?? profile?.currentQuest);

    // `demoBlocks` is sometimes keyed by number and sometimes by string depending on call sites.
    const questBlocks =
      demoBlocks?.[questNo] ?? demoBlocks?.[Number(questNo)] ?? {};

    // `prevBlock` is global across quests; after switching quests it can contain
    // sequences from another quest. Filter it down to only sequences that exist
    // in the currently viewed quest.
    const questSequences = new Set(
      Object.values(questBlocks).map((b: any) => b?.blockPrimarySequence),
    );
    const prevBlockForQuest = (Array.isArray(prevBlock) ? prevBlock : []).filter(
      (seq: any) => questSequences.has(seq),
    );

    // If we filtered anything out, sync state so subsequent "Go Back" clicks
    // don’t keep using the wrong quest’s history.
    if (
      Array.isArray(prevBlock) &&
      prevBlockForQuest.length !== prevBlock.length
    ) {
      setprevBlock(prevBlockForQuest);
    }
    // const Prevquestseq =learnerPlayList?.Prevquestseq[playingQuest];
    const lastSeq = learnerPlayList?.Prevquestseq?.[questNo] ?? [];
    let updateNavigateSeq: any = [...lastSeq];

    updateNavigateSeq.pop();
    setLearnerPlayingDetails((prev: any) => ({
      ...prev,
      Prevquestseq: { ...prev.Prevquestseq, [questNo]: updateNavigateSeq },
    }));

    let prev =
      prevBlockForQuest.length > 0
        ? prevBlockForQuest[prevBlockForQuest.length - 1]
        : lastSeq
        ? lastSeq[lastSeq.length - 1]
        : null;

    let getBlocks: any;
    if (prev) {
      getBlocks = Object.values(questBlocks).filter(
        (item: any) => item?.blockPrimarySequence === prev,
      );
      if (prevBlockForQuest.length === 0 && updateNavigateSeq.length > 0) {
        setprevBlock(updateNavigateSeq);
      }
      // Never mutate React state arrays in-place.
      setprevBlock(prevBlockForQuest.slice(0, -1));
      startTransition(() => {
        if (!getBlocks?.[0]) {
          setCurrentScreenId(13);
          return;
        }
        setData(getBlocks[0]);
        setType(getBlocks[0]?.blockChoosen);
        if (getBlocks[0]?.blockChoosen === 'Interaction') {
          const optionsFiltered = [];
          const primarySequence = getBlocks[0].blockPrimarySequence;

          for (const option of gameInfo.questOptions) {
            if (profileData?.Audiogetlanguage.length > 0) {
              if (option?.qpSequence === primarySequence) {
                const profilesetlan = profileData?.Audiogetlanguage.find(
                  (key: any) => key?.textId === option.qpOptionId,
                );

                if (profilesetlan) {
                  const languagecont = {
                    ...option,
                    qpOptionText: profilesetlan.content,
                  };
                  optionsFiltered.push(languagecont);
                } else {
                  optionsFiltered.push(option);
                }
              }
            } else {
              if (option?.qpSequence === primarySequence) {
                optionsFiltered.push(option);
              }
            }
          }
          if (gameInfo?.gameData?.gameShuffle === 'true') {
            for (let i = optionsFiltered.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [optionsFiltered[i], optionsFiltered[j]] = [
                optionsFiltered[j],
                optionsFiltered[i],
              ];
            }
          }
          setOptions(optionsFiltered);
        }
        if (questState && questState[questNo]) {
          if (questState[questNo] !== 'Started') {
            setProfile((prev: any) => ({
              ...prev,
              replayScore: profile.score,
            }));
          }
        }

        setCurrentScreenId(2);
      });
      return false;
    } else {
      startTransition(() => {
        setCurrentScreenId(13);
      });
    }
  };
 
  useEffect(() => {
    gameInfo?.gameQuest.map((item: any) => {
      const questNoAsString = item.gameQuestNo.toString();
      if (profile?.completedLevels?.includes(questNoAsString)) {
        const scores = profile?.score;
        if (scores !== undefined) {
          const sums: any = {};
          scores.forEach((score: any) => {
            const quest = score.quest;
            if (!sums[quest]) {
              sums[quest] = 0;
            }
            sums[quest] += score.score;
          });
          const getFinalscores = Object.entries(sums).map(([quest, score]) => ({
            quest,
            score,
          }));
          const getscores = getFinalscores.find(
            (row: any) => row.quest == item.gameQuestNo,
          );
          const finalscore = getscores?.score ?? 0;
          if (gameInfo?.gameData?.gameDisableOptionalReplays === 'false') {
            if (item?.gameIsSetMinPassScore === 'true') {
              const getminpassscore = item?.gameMinScore;

              if (
                finalscore >= getminpassscore &&
                finalscore < item?.gameTotalScore &&
                profile.compQuest.includes(questNoAsString)
              ) {
                setQuestState((prevquestdataList: any) => ({
                  ...prevquestdataList,
                  [item.gameQuestNo]: 'replayallowed',
                }));
              } else {
                if (
                  finalscore !== undefined &&
                  profile.compQuest.includes(questNoAsString)
                ) {
                  const status =
                    gameInfo?.gameData?.gameDisableOptionalReplays === 'false'
                      ? 'replayallowed'
                      : 'completed';

                  setQuestState((prevquestdataList: any) => ({
                    ...prevquestdataList,
                    [item.gameQuestNo]: 'replayallowed',
                  }));
                } else {
                  setQuestState((prevquestdataList: any) => ({
                    ...prevquestdataList,
                    [item.gameQuestNo]: 'Started',
                  }));
                }
              }
            } else {
              if (
                finalscore !== undefined &&
                profile.compQuest.includes(questNoAsString)
              ) {
                setQuestState((prevquestdataList: any) => ({
                  ...prevquestdataList,
                  [item.gameQuestNo]: 'replayallowed',
                }));
              } else {
                if (
                  item.gameIsSetMinPassScore === 'false' &&
                  profile.compQuest.includes(questNoAsString)
                ) {
                  setQuestState((prevquestdataList: any) => ({
                    ...prevquestdataList,
                    [item.gameQuestNo]: 'completed',
                  }));
                } else {
                  setQuestState((prevquestdataList: any) => ({
                    ...prevquestdataList,
                    [item.gameQuestNo]: 'Started',
                  }));
                }
              }
            }
          } else {
            if (
              finalscore !== undefined &&
              profile.compQuest.includes(questNoAsString)
            ) {
              setQuestState((prevquestdataList: any) => ({
                ...prevquestdataList,
                [item.gameQuestNo]: 'completed',
              }));
            } else if (
              item.gameIsSetMinPassScore === 'false' &&
              profile.compQuest.includes(questNoAsString)
            ) {
              setQuestState((prevquestdataList: any) => ({
                ...prevquestdataList,
                [item.gameQuestNo]: 'completed',
              }));
            } else {
              setQuestState((prevquestdataList: any) => ({
                ...prevquestdataList,
                [item.gameQuestNo]: 'Started',
              }));
            }
          }
        }
      } else {
        setQuestState((prevquestdataList: any) => ({
          ...prevquestdataList,
          [item.gameQuestNo]: 'locked',
        }));
      }
    });
  }, [profile?.compleLevel]);
const earnedStars = Number(profile?.questStars) || 3;
const totalStars = 3;

const minScore = Number(curretQuestOptions?.gameMinScore) || 0;
const totalScore = Number(curretQuestOptions?.gameTotalScore) || 0;
const userScore = Number(geFinalscorequest) || 0;


const competencyPercentage = Math.min(
  Math.round((getscore / curretQuestOptions?.gameTotalScore) * 100),
  100
);

/**
 * starFill values:
 * 0   = empty
 * 1   = fully filled
 * 0.x = partially filled
 */
// let starFill = [0, 0, 0];

  // Badge logic from original Completion component
  const showBadge = curretQuestOptions?.gameIsSetBadge === 'true';
  const hasBadge = curretQuestOptions?.gameBadge && curretQuestOptions?.gameBadge !== '';
  const hasCriteria = curretQuestOptions?.gameIsSetCriteriaForBadge === 'true';
  const badgeScoreNeeded = curretQuestOptions?.gameAwardBadgeScore || 0;
  const badgeName = curretQuestOptions?.gameBadgeName || '';

  // Check if user earned the badge
  const earnedBadge = hasBadge && (!hasCriteria || (hasCriteria && getscore >= badgeScoreNeeded));
  const missedBadge = showBadge && hasBadge && hasCriteria && getscore < badgeScoreNeeded;

  // Get badge image from preloadedAssets
  const badgeImageKey = `Quest_${currentQuestNo === profile?.currentQuest ? profile?.currentQuest : currentQuestNo}`;
  const badgeImage = preloadedAssets[badgeImageKey];



let starFill = [0, 0, 0];

if (totalScore > 0) {
  const perStar = totalScore / 3;

  starFill[0] = normalizeStarValue(getscore / perStar);

  starFill[1] = normalizeStarValue(
    Math.max(getscore - perStar, 0) / perStar,
  );

  starFill[2] = normalizeStarValue(
    Math.max(getscore - 2 * perStar, 0) / perStar,
  );
}

type QuestType = 1 | 2 | 3 | 4;

const questTitles: Record<QuestType, string> = {
  1: "Goal Setting",
  2: "Delegation",
  3: "Giving Feedback",
  4: "Conflict Management",
};

    const questIcons: Partial<Record<QuestType, JSX.Element>> = {
      1: (
        <svg
          viewBox="0 0 512 512"
          xmlns="http://www.w3.org/2000/svg"
          className="neon-icon w-10 h-10 flex-shrink-0"
          style={
            {
              "--main-color": primaryColor,
            } as React.CSSProperties
          }

        >


          <style>
            {`
   .neon-icon {
     /* Base colour (#00FFFF) expressed as HSL */
     --main-color: var(--main-color);
   
     /* Main colour */
     --main-color: var(--main-color);
   
     /* Programmatic dark variant */
       --dark-color: color-mix(
       in srgb,
       var(--main-color) 70%,
       black
     );
   }
      `}
  </style>

  <defs>
    <clipPath id="clipCircle">
      <circle cx="256" cy="256" r="256"/>
    </clipPath>

    <radialGradient id="bg" cx="50%" cy="50%" r="70%">
      <stop offset="0%" stop-color="#0b0216"/>
      <stop offset="55%" stop-color="#05010b"/>
      <stop offset="100%" stop-color="#000000"/>
    </radialGradient>

    <radialGradient id="haze" cx="50%" cy="55%" r="55%">
      <stop offset="0%" stop-color="var(--main-color)" stop-opacity="0.25"/>
      <stop offset="55%" stop-color="var(--dark-color)" stop-opacity="0.10"/>
      <stop offset="100%" stop-color="#000000" stop-opacity="0"/>
    </radialGradient>

    <linearGradient id="mainGrad" x1="20%" y1="20%" x2="85%" y2="85%">
      <stop offset="0%"   stop-color="var(--main-color)"/>
      <stop offset="100%" stop-color="var(--dark-color)"/>
    </linearGradient>

    <filter id="softGlow" x="-60%" y="-60%" width="220%" height="220%">
      <feGaussianBlur stdDeviation="8" result="b"/>
      <feColorMatrix in="b" type="matrix" values="
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 12 -4" result="g"/>
      <feMerge>
        <feMergeNode in="g"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>

            <filter id="ringGlow" x="-140%" y="-140%" width="380%" height="380%">

              <feGaussianBlur in="SourceGraphic" stdDeviation="18" result="auraBlur" />
              <feColorMatrix in="auraBlur" type="matrix" values="
       1 0 0 0 0
       0 1 0 0 0
       0 0 1 0 0
       0 0 0 4 0" result="aura" />


              <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="midBlur" />
              <feColorMatrix in="midBlur" type="matrix" values="
       1 0 0 0 0
       0 1 0 0 0
       0 0 1 0 0
       0 0 0 2 0" result="mid" />


              <feGaussianBlur in="SourceGraphic" stdDeviation="3.2" result="hotBlur" />
              <feColorMatrix in="hotBlur" type="matrix" values="
       1 0 0 0 0
       0 1 0 0 0
       0 0 1 0 0
       0 0 0 4 0" result="hot" />


              <feMerge>
                <feMergeNode in="aura" />
                <feMergeNode in="mid" />
                <feMergeNode in="hot" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <radialGradient id="vign" cx="50%" cy="50%" r="65%">
              <stop offset="55%" stop-color="#000" stop-opacity="0" />
              <stop offset="100%" stop-color="#000" stop-opacity="0.55" />
            </radialGradient>
          </defs>

          <g clip-path="url(#clipCircle)">

            <rect width="512" height="512" fill="url(#bg)" />
            <rect width="512" height="512" fill="url(#haze)" />

            <g filter="url(#ringGlow)">
              <circle cx="256" cy="256" r="212" fill="none" stroke="url(#mainGrad)" stroke-width="6" opacity="0.35" />
              <circle cx="256" cy="256" r="210" fill="none" stroke="url(#mainGrad)" stroke-width="3.5" opacity="0.75" />

              <circle cx="256" cy="52" r="14" fill="#000000" opacity="0.65" />
              <circle cx="256" cy="52" r="11" fill="none" stroke="var(--main-color)" stroke-width="4" opacity="0.9" />

              <circle cx="256" cy="460" r="14" fill="#000000" opacity="0.65" />
              <circle cx="256" cy="460" r="11" fill="none" stroke="var(--main-color)" stroke-width="4" opacity="0.9" />
            </g>


            <g filter="url(#softGlow)" transform="translate(256 256) scale(13.5) translate(-12 -12)">

              <path d="M21.92,5.62A1,1,0,0,0,21,5H19V3a1,1,0,0,0-.62-.92,1,1,0,0,0-1.09.21l-3,3A1,1,0,0,0,14,6V8.59l-2.21,2.2a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L15.41,10H18a1,1,0,0,0,.71-.29l3-3A1,1,0,0,0,21.92,5.62Z"
                fill="url(#mainGrad)" />

              <path d="M12,22A10,10,0,0,1,12,2h.42a1,1,0,1,1-.18,2H12a8,8,0,1,0,8,8,1.93,1.93,0,0,0,0-.24,1,1,0,0,1,.91-1.09,1,1,0,0,1,1.09.91c0,.14,0,.28,0,.42A10,10,0,0,1,12,22Zm5.88-8.8a1,1,0,0,0-2-.4A4,4,0,1,1,11.2,8.08a1,1,0,1,0-.4-2,6,6,0,1,0,7.08,7.08Z"
                fill="url(#mainGrad)" />
            </g>

            <rect width="512" height="512" fill="url(#vign)" />

          </g>
        </svg>
      ),
      2: (
        <svg width="512" height="512" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" className="neon-icon w-10 h-10 flex-shrink-0"
          style={
            {
              "--main-color": primaryColor,
            } as React.CSSProperties
          }>

          <style>
            {`
   .neon-icon {
     /* Base colour (#00FFFF) expressed as HSL */
     --main-color: var(--main-color);
   
     /* Main colour */
     --main-color: var(--main-color);
   
     /* Programmatic dark variant */
       --dark-color: color-mix(
       in srgb,
       var(--main-color) 70%,
       black
     );
   }
      `}
          </style>

          <defs>
            <clipPath id="clipCircle">
              <circle cx="256" cy="256" r="256" />
            </clipPath>

            <radialGradient id="bg" cx="50%" cy="50%" r="70%">
              <stop offset="0%" stop-color="#0b0216" />
              <stop offset="55%" stop-color="#05010b" />
              <stop offset="100%" stop-color="#000000" />
            </radialGradient>

            <radialGradient id="haze" cx="50%" cy="55%" r="55%">
              <stop offset="0%" stop-color="var(--main-color)" stop-opacity="0.25" />
              <stop offset="55%" stop-color="var(--dark-color)" stop-opacity="0.10" />
              <stop offset="100%" stop-color="#000000" stop-opacity="0" />
            </radialGradient>

            <linearGradient id="mainGrad" x1="20%" y1="20%" x2="85%" y2="85%">
              <stop offset="0%" stop-color="var(--main-color)" />
              <stop offset="100%" stop-color="var(--dark-color)" />
            </linearGradient>

            <filter id="softGlow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="8" result="b" />
              <feColorMatrix in="b" type="matrix" values="
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 12 -4" result="g" />
              <feMerge>
                <feMergeNode in="g" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>

            <filter id="ringGlow" x="-140%" y="-140%" width="380%" height="380%">

              <feGaussianBlur in="SourceGraphic" stdDeviation="18" result="auraBlur" />
              <feColorMatrix in="auraBlur" type="matrix" values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0" result="aura"/>


  <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="midBlur"/>
  <feColorMatrix in="midBlur" type="matrix" values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 2 0" result="mid"/>


  <feGaussianBlur in="SourceGraphic" stdDeviation="3.2" result="hotBlur"/>
  <feColorMatrix in="hotBlur" type="matrix" values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0" result="hot"/>


  <feMerge>
    <feMergeNode in="aura"/>
    <feMergeNode in="mid"/>
    <feMergeNode in="hot"/>
    <feMergeNode in="SourceGraphic"/>
  </feMerge>
</filter>

    <radialGradient id="vign" cx="50%" cy="50%" r="65%">
      <stop offset="55%" stop-color="#000" stop-opacity="0"/>
      <stop offset="100%" stop-color="#000" stop-opacity="0.55"/>
    </radialGradient>
  </defs>

  <g clip-path="url(#clipCircle)">
    <rect width="512" height="512" fill="url(#bg)"/>
    <rect width="512" height="512" fill="url(#haze)"/>

    <g filter="url(#ringGlow)">
      <circle cx="256" cy="256" r="212" fill="none" stroke="url(#mainGrad)" stroke-width="6" opacity="0.35"/>
      <circle cx="256" cy="256" r="210" fill="none" stroke="url(#mainGrad)" stroke-width="3.5" opacity="0.75"/>

      <circle cx="256" cy="52" r="14" fill="#000000" opacity="0.65"/>
      <circle cx="256" cy="52" r="11" fill="none" stroke="var(--main-color)" stroke-width="4" opacity="0.9"/>

      <circle cx="256" cy="460" r="14" fill="#000000" opacity="0.65"/>
      <circle cx="256" cy="460" r="11" fill="none" stroke="var(--main-color)" stroke-width="4" opacity="0.9"/>
    </g>

  
    <g filter="url(#softGlow)"
       transform="translate(256 256) rotate(90) scale(14) translate(-12 -12)">
      <path fill-rule="evenodd" clip-rule="evenodd"
            d="M13.803 5.33333C13.803 3.49238 15.3022 2 17.1515 2C19.0008 2 20.5 3.49238 20.5 5.33333C20.5 7.17428 19.0008 8.66667 17.1515 8.66667C16.2177 8.66667 15.3738 8.28596 14.7671 7.67347L10.1317 10.8295C10.1745 11.0425 10.197 11.2625 10.197 11.4872C10.197 11.9322 10.109 12.3576 9.94959 12.7464L15.0323 16.0858C15.6092 15.6161 16.3473 15.3333 17.1515 15.3333C19.0008 15.3333 20.5 16.8257 20.5 18.6667C20.5 20.5076 19.0008 22 17.1515 22C15.3022 22 13.803 20.5076 13.803 18.6667C13.803 18.1845 13.9062 17.7255 14.0917 17.3111L9.05007 13.9987C8.46196 14.5098 7.6916 14.8205 6.84848 14.8205C4.99917 14.8205 3.5 13.3281 3.5 11.4872C3.5 9.64623 4.99917 8.15385 6.84848 8.15385C7.9119 8.15385 8.85853 8.64725 9.47145 9.41518L13.9639 6.35642C13.8594 6.03359 13.803 5.6896 13.803 5.33333Z"
            fill="url(#mainGrad)"/>
    </g>

    <rect width="512" height="512" fill="url(#vign)"/>
  </g>
</svg>

  ),
  3:(
    <svg width="512" height="512" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg"  
     className="neon-icon w-10 h-10 flex-shrink-0"
    style={
       {
         "--main-color": primaryColor,
       } as React.CSSProperties
     }>
  
  <style>
 {`
   .neon-icon {
     /* Base colour (#00FFFF) expressed as HSL */
     --main-color: var(--main-color);
   
     /* Main colour */
     --main-color: var(--main-color);
   
     /* Programmatic dark variant */
       --dark-color: color-mix(
       in srgb,
       var(--main-color) 70%,
       black
     );
   }
      `}
  </style>
  <defs>
    <clipPath id="clipCircle">
      <circle cx="256" cy="256" r="256"/>
    </clipPath>

    <radialGradient id="bg" cx="50%" cy="50%" r="70%">
      <stop offset="0%" stop-color="#0b0216"/>
      <stop offset="55%" stop-color="#05010b"/>
      <stop offset="100%" stop-color="#000000"/>
    </radialGradient>

    <radialGradient id="haze" cx="50%" cy="55%" r="55%">
      <stop offset="0%" stop-color="var(--main-color)" stop-opacity="0.25"/>
      <stop offset="55%" stop-color="var(--dark-color)" stop-opacity="0.10"/>
      <stop offset="100%" stop-color="#000000" stop-opacity="0"/>
    </radialGradient>

    <linearGradient id="mainGrad" x1="20%" y1="20%" x2="85%" y2="85%">
      <stop offset="0%"   stop-color="var(--main-color)"/>
      <stop offset="100%" stop-color="var(--dark-color)"/>
    </linearGradient>

    <filter id="softGlow" x="-60%" y="-60%" width="220%" height="220%">
      <feGaussianBlur stdDeviation="8" result="b"/>
      <feColorMatrix in="b" type="matrix" values="
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 12 -4" result="g"/>
      <feMerge>
        <feMergeNode in="g"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>

    <filter id="ringGlow" x="-60%" y="-60%" width="220%" height="220%">
      <feGaussianBlur stdDeviation="3.5" result="b"/>
      <feMerge>
        <feMergeNode in="b"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>

 <filter id="ringGlow" x="-140%" y="-140%" width="380%" height="380%">

  <feGaussianBlur in="SourceGraphic" stdDeviation="18" result="auraBlur"/>
  <feColorMatrix in="auraBlur" type="matrix" values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0" result="aura"/>

 
  <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="midBlur"/>
  <feColorMatrix in="midBlur" type="matrix" values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 2 0" result="mid"/>


  <feGaussianBlur in="SourceGraphic" stdDeviation="3.2" result="hotBlur"/>
  <feColorMatrix in="hotBlur" type="matrix" values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0" result="hot"/>

 
  <feMerge>
    <feMergeNode in="aura"/>
    <feMergeNode in="mid"/>
    <feMergeNode in="hot"/>
    <feMergeNode in="SourceGraphic"/>
  </feMerge>
</filter>

    <radialGradient id="vign" cx="50%" cy="50%" r="65%">
      <stop offset="55%" stop-color="#000" stop-opacity="0"/>
      <stop offset="100%" stop-color="#000" stop-opacity="0.55"/>
    </radialGradient>
  </defs>

  <g clip-path="url(#clipCircle)">

    <rect width="512" height="512" fill="url(#bg)"/>
    <rect width="512" height="512" fill="url(#haze)"/>

    <g filter="url(#ringGlow)">
      <circle cx="256" cy="256" r="212" fill="none" stroke="url(#mainGrad)" stroke-width="6" opacity="0.35"/>
      <circle cx="256" cy="256" r="210" fill="none" stroke="url(#mainGrad)" stroke-width="3.5" opacity="0.75"/>

      <circle cx="256" cy="52" r="14" fill="#000000" opacity="0.65"/>
      <circle cx="256" cy="52" r="11" fill="none" stroke="var(--main-color)" stroke-width="4" opacity="0.9"/>

      <circle cx="256" cy="460" r="14" fill="#000000" opacity="0.65"/>
      <circle cx="256" cy="460" r="11" fill="none" stroke="var(--main-color)" stroke-width="4" opacity="0.9"/>
    </g>

    <g filter="url(#softGlow)" transform="translate(256 256) scale(17) translate(-8 -8)">
      <path d="m4.75 10.75h-3m12.5-2c0 3-2.79822 5.5-6.25 5.5-3.75 0-6.25-3.5-6.25-3.5v3.5m9.5-9h3m-12.5 2c0-3 2.79822-5.5 6.25-5.5 3.75 0 6.25 3.5 6.25 3.5v-3.5"
            fill="none"
            stroke="url(#mainGrad)"
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"/>
    </g>

    <rect width="512" height="512" fill="url(#vign)"/>

  </g>
</svg>
  ),
  4:(
    <svg width="512" height="512" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" 
     className="neon-icon w-10 h-10 flex-shrink-0"
    style={
       {
         "--main-color": primaryColor,
       } as React.CSSProperties
     }>
  
  <style>
 {`
   .neon-icon {
     /* Base colour (#00FFFF) expressed as HSL */
     --main-color: var(--main-color);
   
     /* Main colour */
     --main-color: var(--main-color);
   
     /* Programmatic dark variant */
       --dark-color: color-mix(
       in srgb,
       var(--main-color) 70%,
       black
     );
   }
      `}
  </style>
  <defs>
    <clipPath id="clipCircle">
      <circle cx="256" cy="256" r="256"/>
    </clipPath>

    <radialGradient id="bg" cx="50%" cy="50%" r="70%">
      <stop offset="0%" stop-color="#0b0216"/>
      <stop offset="55%" stop-color="#05010b"/>
      <stop offset="100%" stop-color="#000000"/>
    </radialGradient>

    <radialGradient id="haze" cx="50%" cy="55%" r="55%">
      <stop offset="0%" stop-color="var(--main-color)" stop-opacity="0.25"/>
      <stop offset="55%" stop-color="var(--dark-color)" stop-opacity="0.10"/>
      <stop offset="100%" stop-color="#000000" stop-opacity="0"/>
    </radialGradient>

    <linearGradient id="mainGrad" x1="20%" y1="20%" x2="85%" y2="85%">
      <stop offset="0%"   stop-color="var(--main-color)"/>
      <stop offset="100%" stop-color="var(--dark-color)"/>
    </linearGradient>

    <filter id="softGlow" x="-60%" y="-60%" width="220%" height="220%">
      <feGaussianBlur stdDeviation="8" result="b"/>
      <feColorMatrix in="b" type="matrix" values="
        1 0 0 0 0
        0 1 0 0 0
        0 0 1 0 0
        0 0 0 9 -4" result="g"/>
      <feMerge>
        <feMergeNode in="g"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>

<filter id="ringGlow" x="-140%" y="-140%" width="380%" height="380%">

  <feGaussianBlur in="SourceGraphic" stdDeviation="18" result="auraBlur"/>
  <feColorMatrix in="auraBlur" type="matrix" values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0" result="aura"/>

  
  <feGaussianBlur in="SourceGraphic" stdDeviation="8" result="midBlur"/>
  <feColorMatrix in="midBlur" type="matrix" values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 2 0" result="mid"/>

 
  <feGaussianBlur in="SourceGraphic" stdDeviation="3.2" result="hotBlur"/>
  <feColorMatrix in="hotBlur" type="matrix" values="
    1 0 0 0 0
    0 1 0 0 0
    0 0 1 0 0
    0 0 0 4 0" result="hot"/>

 
  <feMerge>
    <feMergeNode in="aura"/>
    <feMergeNode in="mid"/>
    <feMergeNode in="hot"/>
    <feMergeNode in="SourceGraphic"/>
  </feMerge>
</filter>

    <radialGradient id="vign" cx="50%" cy="50%" r="65%">
      <stop offset="55%" stop-color="#000" stop-opacity="0"/>
      <stop offset="100%" stop-color="#000" stop-opacity="0.55"/>
    </radialGradient>
  </defs>

  <g clip-path="url(#clipCircle)">
    <rect width="512" height="512" fill="url(#bg)"/>
    <rect width="512" height="512" fill="url(#haze)"/>

    <g filter="url(#ringGlow)">
      <circle cx="256" cy="256" r="212" fill="none" stroke="url(#mainGrad)" stroke-width="6" opacity="0.35"/>
      <circle cx="256" cy="256" r="210" fill="none" stroke="url(#mainGrad)" stroke-width="3.5" opacity="0.75"/>

      <circle cx="256" cy="52" r="14" fill="#000000" opacity="0.65"/>
      <circle cx="256" cy="52" r="11" fill="none" stroke="var(--main-color)" stroke-width="4" opacity="0.9"/>

      <circle cx="256" cy="460" r="14" fill="#000000" opacity="0.65"/>
      <circle cx="256" cy="460" r="11" fill="none" stroke="var(--main-color)" stroke-width="4" opacity="0.9"/>
    </g>

   
    <g filter="url(#softGlow)" transform="translate(256 256) scale(12) translate(-12 -12)">
      <path d="M3 11.9914C3 17.6294 7.23896 20.3655 9.89856 21.5273C10.62 21.8424 10.9807 22 12 22V8L3 11V11.9914Z"
            fill="url(#mainGrad)"/>
      <path opacity="0.95" d="M14.1014 21.5273C16.761 20.3655 21 17.6294 21 11.9914V11L12 8V22C13.0193 22 13.38 21.8424 14.1014 21.5273Z"
            fill="url(#mainGrad)"/>
      <path opacity="0.95" d="M8.83772 2.80472L8.26491 3.00079C5.25832 4.02996 3.75503 4.54454 3.37752 5.08241C3 5.62028 3 7.21907 3 10.4167V11L12 8V2C11.1886 2 10.405 2.26824 8.83772 2.80472Z"
            fill="url(#mainGrad)"/>
      <path d="M15.7351 3.00079L15.1623 2.80472C13.595 2.26824 12.8114 2 12 2V8L21 11V10.4167C21 7.21907 21 5.62028 20.6225 5.08241C20.245 4.54454 18.7417 4.02996 15.7351 3.00079Z"
            fill="url(#mainGrad)"/>
    </g>

    <rect width="512" height="512" fill="url(#vign)"/>
  </g>
</svg>

  )
};

const title = questTitles[(profile?.currentQuest as QuestType) ?? 1];
const icon = questIcons[(profile?.currentQuest as QuestType) ?? 1];
    const TIMELINE = {
      title: 0,
      titleSound: 200,
      container: 800,
      description: 1200,
      rewardsHeader: 1600,
      pointsCard: 2000,
      pointsAnimation: 2400,
      starsCard: 3200,
      starsAnimation: 3600,
      badgeCard: 4800,
      badgeStamp: 5200,
      meter: 6000,
      meterAnimation: 6400,
    };

    const FINAL_PHASE = 7;

    const [showTransition, setShowTransition] = useState(false);
    const [started, setStarted] = useState(false);
    const [phase, setPhase] = useState(0);
    const [isShaking, setIsShaking] = useState(false);
    const timersRef = useRef<NodeJS.Timeout[]>([]);

    const handleSkip = useCallback(() => {
      // Clear all pending timers
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];

      // Disable sounds temporarily to prevent cacophony
      setEnabled(false);

      // Set to final phase immediately
      setPhase(FINAL_PHASE);

      // Re-enable sounds after a short delay
      setTimeout(() => setEnabled(true), 100);
    }, [setEnabled]);

    useEffect(() => {
      if (!started) return;

      const timers: NodeJS.Timeout[] = [];

      // Phase 1: Title appears
      timers.push(setTimeout(() => setPhase(1), TIMELINE.title));

      // Play Quest Complete sound when title appears
      timers.push(setTimeout(() => playSound('questComplete'), TIMELINE.titleSound));

      // Phase 2: Container and description
      timers.push(setTimeout(() => setPhase(2), TIMELINE.container));

      // Phase 3: Rewards header
      timers.push(setTimeout(() => setPhase(3), TIMELINE.rewardsHeader));

      // Phase 4: Points card
      timers.push(setTimeout(() => {
        setPhase(4);
        playSound('cardReveal');
      }, TIMELINE.pointsCard));

      // Points counting sound
      timers.push(setTimeout(() => playSound('pointsUp'), TIMELINE.pointsAnimation));

      // Phase 5: Stars card
      timers.push(setTimeout(() => {
        setPhase(5);
        playSound('cardReveal');
      }, TIMELINE.starsCard));

      // Phase 6: Badge card
      timers.push(setTimeout(() => {
        setPhase(6);
        playSound('cardReveal');
      }, TIMELINE.badgeCard));

      // Badge stamp sound
      timers.push(setTimeout(() => playSound('stamp'), TIMELINE.badgeStamp));

      // Phase 7: Mastery meter
      timers.push(setTimeout(() => {
        setPhase(7);
        playSound('cardReveal');
      }, TIMELINE.meter));

      // Power up sound
      timers.push(setTimeout(() => playSound('powerup'), TIMELINE.meterAnimation));

      // Store timers ref for skip functionality
      timersRef.current = timers;

      return () => timers.forEach(clearTimeout);
    }, [started, playSound]);

    const handleTransitionComplete = useCallback(() => {
      setShowTransition(false);
      setStarted(true);
      // Play impact sound and trigger shake
      playSound('transitionImpact');
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 150);
    }, [playSound]);

    useEffect(() => {
      if (!showTransition) {
        setShowTransition(true);
        playSound("hudSync");
      }
    }, [])

    const handleStarIgnite = useCallback(() => {
      playSound('chime');
    }, [playSound]);

    const [animatedWidth, setAnimatedWidth] = useState(0);
    const [startCount, setStartCount] = useState(false);
    const visible = phase >= 7
    const animationDelay = TIMELINE.meterAnimation - TIMELINE.meter
    useEffect(() => {
      if (!visible) return;

      const timer = setTimeout(() => {
        setAnimatedWidth(competencyPercentage);
        setStartCount(true);
      }, animationDelay);

      return () => clearTimeout(timer);
    }, [visible, competencyPercentage, animationDelay]);
    const animatedPercentage = useCountUp({
      end: startCount ? competencyPercentage : 0,
      duration: 1500,
      delay: 0,
    });
  return (
      <>
        {showTransition && (
          <HudTransition onComplete={handleTransitionComplete} />
        )}
        
        {started && (
          <div
            className={`relative h-screen w-full bg-cover bg-center bg-no-repeat font-rubik ${isShaking ? 'screen-shake' : ''}`}
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=2564&auto=format&fit=crop')",
            }}
          // className="relative w-full h-screen bg-black"
          //  style={{ backgroundImage: `url('${imgBackground}')` }}
          >
            <svg
              style={{ width: 0, height: 0, position: "absolute" }}
              aria-hidden="true"
            >
              <defs>
                <linearGradient id="plasmaGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#FFFFFF" />
                  <stop offset="20%" stopColor="#00f2ff" />
                  <stop offset="100%" stopColor="#009199" />
                </linearGradient>
              </defs>
            </svg>

            {/* HUD Effects */}
            <div className="scan-bar" />
            <div className="hud-grid" />

            {/* Dark Overlay */}
            <div className="absolute inset-0 bg-black/90" />

            {/* Particle Field */}
            <ParticleField />

            {/* Background Beam */}
            <BackgroundBeam />
   

      <div className="w-full px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 lg:pb-8 relative h-full flex">
        {/* Top Navigation Bar */}
        <div className="absolute top-0 left-0 right-0 h-[5.5rem] flex items-center justify-between bg-cover bg-center bg-no-repeat px-4 sm:px-6 lg:px-8">
          
          <div className="flex items-center gap-4">
            
            <Dialog>
              <DialogTrigger asChild>
                
              </DialogTrigger>
              <DialogContent
                className="sm:max-w-2xl rounded-[2rem] px-7 py-6 border-none shadow-lg"
                style={{
                  background: `linear-gradient(151.477deg, rgb(0,0,0) 17.606%, rgba(${hexToRgb(
                    primaryColor
                  )}, 0.6) 218.68%)`,
                  border: `1px solid linear-gradient(151.477deg, rgb(0, 0, 0) 17.606%, rgba(${hexToRgb(
                    primaryColor
                  )}, 0.4) 188.68%)`,
                  boxShadow: "0px 0px 23.4px 0px rgba(0, 0, 0, 0.50)",
                }}
              >
                <h2
                  className="text-center text-3xl py-1 font-medium"
                  style={{
                    color: primaryColor,
                    textShadow: `0 0 9px rgba(${hexToRgb(primaryColor)}, 0.49)`,
                  }}
                >
                  Settings
                </h2>

                {/* Decorative Line */}
                <div className="flex items-center justify-center relative w-full">
                  <div className="w-full">
                    <div className="h-[1px] relative w-full">
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(90deg, transparent 0%, ${primaryColor} 48%, transparent 100%)`,
                          opacity: 0.995,
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-8 mt-3 mb-2 max-w-md mx-auto w-full pt-5 pb-3">
                  <div className="flex flex-col gap-10">
                    {/* Music Volume */}
                    <div className="flex flex-col gap-1 items-center">
                      <label className="text-2xl font-rubik mb-2 text-white font-normal tracking-[-0.005rem]">
                        Music Volume
                      </label>
                      <Slider
                        defaultValue={[40]}
                        max={100}
                        step={1}
                        className="w-full"
                        // trackClassName="bg-white/70"
                        // thumbClassName="bg-white border border-gray-300 shadow-sm"
                      />
                    </div>
                    {/* Voice Over Volume */}
                    <div className="flex flex-col gap-1 items-center">
                      <label className="text-2xl font-rubik mb-2 text-white font-normal tracking-[-0.005rem]">
                        Voice over volume
                      </label>
                      <Slider
                        defaultValue={[20]}
                        max={100}
                        step={1}
                        className="w-full"
                        // trackClassName="bg-white/70"
                        // thumbClassName="bg-white border border-gray-300 shadow-sm"
                      />
                    </div>
                    {/* Color Picker */}
                    <div className="flex flex-col gap-1 items-center">
                      <label className="text-2xl font-rubik mb-2 text-white font-normal tracking-[-0.005rem]">
                        Set Theme
                      </label>
                      <ColorPicker />
                    </div>
                  </div>

                  {/* Okay button */}
                  <DialogTrigger asChild>
                    <Buttons
                      variant="ghost"
                      size="sm"
                      className="w-[5.75rem] text-lg h-11 py-2 px-4 rounded-4xl transition-all duration-200 hover:scale-110 text-white hover:text-white mx-auto"
                      style={{
                        background: `linear-gradient(275.041deg
                              ,
                                rgba(${hexToRgb(
                                  primaryColor
                                )}, 0.7) 7.25%, rgba(0, 0, 0, 0.8) 84.803%)`,
                        border: `1px solid ${primaryColor}`,
                        boxShadow: `0px 0px 15.9542px 0px rgba(3, 51, 38, 0.8)`,
                      }}
                    >
                      Okay
                    </Buttons>
                  </DialogTrigger>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

          

                                           <div className="relative z-10 w-full min-h-screen flex flex-col overflow-y-auto">
  <div className="flex-grow flex flex-col items-center justify-center gap-5 py-8 px-4">

    {/* TITLE */}
    <div
      className={`text-center relative transition-all duration-700 ${
        phase >= 1 ? "opacity-100 scale-100" : "opacity-0 scale-90"
      }`}
    >
      {phase >= 1 && <TitlePulse />}

      <h1
        className="font-orbitron text-4xl font-bold uppercase tracking-[0.08px] leading-tight text-glow"
        style={{ color: primaryColor }}
      >
        Quest
        <br />
        Complete
      </h1>
    </div>

    {/* MAIN CONTAINER */}
 <div
                    className={`relative w-[60.75rem] backdrop-blur-[12.5rem] bg-black/20 rounded-[1.4rem] p-6 border transition-all duration-700 ${phase >= 2 ? 'opacity-100 translate-y-0 container-power-up' : 'opacity-0 translate-y-8'}`}
                    style={{
                      borderColor: "color-mix(in srgb, var(--dynamic-primary) 20%, transparent)",
                      boxShadow: "0 3.58px 19.689px 0 color-mix(in srgb, var(--dynamic-primary) 20%, transparent)"
                    }}
                  >
      {/* QUEST MESSAGE */}
      <div
        className={`max-w-xl mx-auto mb-6 transition-all duration-500 ${
          phase >= 2 ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
        }`}
        style={{ transitionDelay: "200ms" }}
      >
        <p className="text-center text-lg font-normal text-white tracking-[-0.08px]">
          {quetCompletionMessage}
        </p>
      </div>

      {/* REWARDS TITLE */}
      <div
        className={`flex items-center gap-5 mb-6 transition-all duration-500 ${
          phase >= 3 ? "opacity-100" : "opacity-0"
        }`}
      >
        <div
          style={
            {
              "--dynamic-primary": hexToRgb(primaryColor),
            } as React.CSSProperties
          }
          className="h-[1.5px] flex-1 bg-gradient-to-r from-transparent via-[rgba(var(--dynamic-primary),1)] to-transparent opacity-40"
        />

        <span
          className="text-base uppercase tracking-[0.4em] font-orbitron font-bold text-glow-strong"
          style={{ color: primaryColor }}
        >
          Rewards
        </span>

        <div
          style={
            {
              "--dynamic-primary": hexToRgb(primaryColor),
            } as React.CSSProperties
          }
          className="h-[1.5px] flex-1 bg-gradient-to-r from-transparent via-[rgba(var(--dynamic-primary),1)] to-transparent opacity-40"
        />
      </div>

      {/* REWARD CARDS */}
      <div className="flex flex-wrap gap-3 justify-center mb-6 overflow-visible">

        <RewardCard
          title="Points"
          progress={Number(getscore) || 0}
          maxProgress={Number(curretQuestOptions?.gameTotalScore) || 0}
          icon="points"
          visible={phase >= 4}
          animateValue={phase >= 4}
          animationDelay={TIMELINE.pointsAnimation - TIMELINE.pointsCard}
        />

        <StarsCard
          filledStars={starFill}
          totalStars={3}
          visible={phase >= 5}
          animationDelay={TIMELINE.starsAnimation - TIMELINE.starsCard}
          onStarIgnite={handleStarIgnite}
        />

        {showBadge ? (
          <RewardCard
            title={badgeName}
            icon="badge"
            badgeImage={earnedBadge ? badgeImage : undefined}
            showMissedMessage={missedBadge}
            visible={phase >= 6}
            animateStamp={phase >= 6}
            stampDelay={TIMELINE.badgeStamp - TIMELINE.badgeCard}
          />
        ) : (
          <RewardCard
            title="Badge"
            icon="trophy"
            visible={phase >= 6}
            animateStamp={phase >= 6}
            stampDelay={TIMELINE.badgeStamp - TIMELINE.badgeCard}
          />
        )}

      </div>

      {/* PROGRESS SECTION */}
      <div
        className={`mt-8 px-10 pb-10 flex gap-5 items-center transition-all duration-500 ${
          visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >

                      {/* Goal Icon */}
                      <div className="w-14 h-14 flex-shrink-0 goal-icon-container">
                        <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                          <defs>

                            <clipPath id="clipCircle">
                              <circle cx="256" cy="256" r="256" />
                            </clipPath>

                            <radialGradient id="bg" cx="50%" cy="50%" r="70%">
                              <stop offset="0%" stopColor="#050807" />
                              <stop offset="100%" stopColor="#000000" />
                            </radialGradient>

                            {/* HAZE GLOW */}
                            <radialGradient id="haze" cx="50%" cy="55%" r="55%">
                              <stop offset="0%" stopColor={`rgba(${hexToRgb(primaryColor)},0.3)`} />
                              <stop offset="100%" stopColor="rgba(0,0,0,0)" />
                            </radialGradient>

                            {/* MAIN ICON GRADIENT */}
                            <linearGradient id="mainGradIcon" x1="20%" y1="20%" x2="85%" y2="85%">
                              <stop offset="0%" stopColor={primaryColor} />
                              <stop offset="100%" stopColor={`rgba(${hexToRgb(primaryColor)},0.65)`} />
                            </linearGradient>

                          </defs>

                          <g clipPath="url(#clipCircle)">
                            <rect width="512" height="512" fill="url(#bg)" />
                            <rect width="512" height="512" fill="url(#haze)" />

                            <g opacity="0.9">
                              <circle cx="256" cy="52" r="16" fill={primaryColor} />
                              <circle cx="256" cy="460" r="16" fill={primaryColor} />
                            </g>

                            <g transform="translate(256 256) scale(14) translate(-12 -12)">
                              <path
                                d="M21.92,5.62A1,1,0,0,0,21,5H19V3a1,1,0,0,0-.62-.92,1,1,0,0,0-1.09.21l-3,3A1,1,0,0,0,14,6V8.59l-2.21,2.2a1,1,0,0,0,0,1.42,1,1,0,0,0,1.42,0L15.41,10H18a1,1,0,0,0,.71-.29l3-3A1,1,0,0,0,21.92,5.62Z"
                                fill="url(#mainGradIcon)"
                              />

                              <path
                                d="M12,22A10,10,0,0,1,12,2h.42a1,1,0,1,1-.18,2H12a8,8,0,1,0,8,8,1.93,1.93,0,0,0,0-.24,1,1,0,0,1,.91-1.09,1,1,0,0,1,1.09.91c0,.14,0,.28,0,.42A10,10,0,0,1,12,22Zm5.88-8.8a1,1,0,0,0-2-.4A4,4,0,1,1,11.2,8.08a1,1,0,1,0-.4-2,6,6,0,1,0,7.08,7.08Z"
                                fill="url(#mainGradIcon)"
                              />
                            </g>
                          </g>
                        </svg>
                      </div>

        <div className="flex-grow">

          {/* HEADER */}
          <div className="flex justify-between items-end mb-2">
            <span
              className="font-orbitron text-[10px] uppercase tracking-[0.2em]"
              style={{ color: primaryColor }}
            >
              {title}
            </span>

            <span className="text-white text-[10px] font-medium font-orbitron tracking-widest tabular-nums">
              {animatedPercentage}% MASTERED
            </span>
          </div>

          {/* PROGRESS BAR */}
          <div
            className="relative h-3 w-full overflow-hidden"
            style={{
              background: `rgba(${hexToRgb(primaryColor)},0.15)`,
              border: `1px solid rgba(${hexToRgb(primaryColor)},0.35)`,
              borderRadius: "999px",
            }}
          >
            <motion.div
              className="absolute inset-y-0 left-0 rounded-full"
              initial={{ width: "0%" }}
              animate={{ width: `${animatedPercentage}%` }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              style={{
                background: `linear-gradient(90deg, rgba(${hexToRgb(
                  primaryColor
                )},0.6), rgba(${hexToRgb(primaryColor)},1))`,
                boxShadow: `0 0 12px rgba(${hexToRgb(primaryColor)},0.85)`,
              }}
            />

            <div className="absolute inset-0 flex justify-evenly pointer-events-none">
              <div className="h-full w-[1.5px] bg-black/40" />
              <div className="h-full w-[1.5px] bg-black/40" />
            </div>
          </div>

          {/* LEVEL LABELS */}
          <div
            className="flex justify-between mt-2 text-[8px] uppercase font-bold tracking-[0.1em] opacity-60 font-orbitron"
            style={{ color: primaryColor }}
          >
            <span className="w-1/3 text-left">Novice</span>
            <span className="w-1/3 text-center">Adept</span>
            <span className="w-1/3 text-right">Master</span>
          </div>

        </div>
      </div>

      {/* NAVIGATION */}
      <div className="flex w-full items-center justify-between">
        <NavigationArrow
          direction="left"
          onClick={() => {
            playMenuSound();
            previousNavigation();
          }}
        />

        <NavigationArrow
          direction="right"
          onClick={() => {
            playMenuSound();

            const questKey = `quest${profile?.currentQuest}`;

            completedVideoRef.current[questKey] = true;

            setIsCompletedVideoMap((prev: any) => ({
              ...prev,
              [questKey]: true,
            }));

            getData(data);
          }}
        />
      </div>

    </div>
  </div>
</div>

            </div>

         
          </div>
        )}
      </>
    );
  };
export default QuestCompletionFutureTheme;